<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  connector: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'sync', 'manageTools']);

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => [
  {
    label: t('CAPTAIN.MCP_CONNECTORS.TOOLS.HEADER'),
    value: 'manage-tools',
    action: 'manage-tools',
    icon: 'i-lucide-wrench',
  },
  {
    label: t('CAPTAIN.MCP_CONNECTORS.EDIT.TITLE'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('CAPTAIN.MCP_CONNECTORS.DELETE.TITLE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const toolsCount = computed(() => props.connector.tools_count ?? 0);

const handleAction = ({ action }) => {
  toggleDropdown(false);
  if (action === 'manage-tools') {
    emit('manageTools', props.connector);
  } else if (action === 'edit') {
    emit('edit', props.connector);
  } else if (action === 'delete') {
    emit('delete', props.connector);
  }
};

const handleSync = () => emit('sync', props.connector);
</script>

<template>
  <CardLayout class="relative">
    <div class="flex relative justify-between w-full gap-1">
      <span class="text-base text-n-slate-12 line-clamp-1 font-medium">
        {{ connector.title }}
      </span>
      <div class="flex items-center gap-2">
        <Policy
          v-on-clickaway="() => toggleDropdown(false)"
          :permissions="['administrator']"
          class="relative flex items-center gap-1 group"
        >
          <Button
            icon="i-lucide-refresh-cw"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            :label="t('CAPTAIN.MCP_CONNECTORS.SYNC.BUTTON')"
            @click="handleSync"
          />
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:right-0 top-full"
            @action="handleAction($event)"
          />
        </Policy>
      </div>
    </div>
    <div class="flex items-center justify-between w-full gap-4 min-w-0">
      <span class="text-sm truncate text-n-slate-11">
        {{ connector.endpoint_url }}
      </span>
      <span
        class="text-xs shrink-0 text-n-slate-11 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-n-alpha-2"
      >
        <i class="i-lucide-wrench text-sm" />
        {{ toolsCount }}
      </span>
    </div>
    <div
      v-if="connector.last_error"
      class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-n-amber-2 text-n-amber-11"
    >
      <span class="i-lucide-triangle-alert size-3.5 shrink-0" />
      <span class="line-clamp-2">{{ connector.last_error }}</span>
    </div>
  </CardLayout>
</template>
